export const DASHBOARD_FEED_FLAG_KEY = "dashboard_feed";
export const IN_APP_NOTIFICATIONS_FLAG_KEY = "dashboard_in_app_notifications";
// SILL-505 — gates the SMS notification-preferences block on the Settings
// page (opt-in toggle + phone display). Fail-closed like SILL-504's
// marketplace gate: see useSmsPreferencesEnabled for the resolver.
// PostHog: Production id 772199 (disabled until Twilio A2P approval),
// Staging id 772196 (enabled for QA); 100% rollout when on.
export const SMS_PREFERENCES_ENABLED_FLAG_KEY = "sms_preferences_enabled";
// SILL-504 — THE single consumer marketplace launch gate (PostHog Production
// id 746059, disabled, 100% when on). Gates every consumer marketplace
// surface: routes via MarketplaceRouteGate, sidebar Marketplace items via
// useDashboardShellCapabilities, publish/go-live copy via
// useMarketplaceState. The M5-era `marketplace_offers_enabled` /
// `marketplace_browse_enabled` keys are retired — they never existed in
// PostHog, and the offers resolver's fail-open default (`flag !== false`)
// left money-path routes reachable in production with no flag flipped.
// Launch = flip this one flag to 100%.
export const MARKETPLACE_GO_LIVE_ENABLED_FLAG_KEY =
  "marketplace_go_live_enabled";
// M3 marketplace onboarding funnel (plan 199). Governs exposure only —
// resolvePosture (components/funnel/resolve-posture.ts) is the single place
// that reads this flag to decide whether the funnel engine or the legacy
// wizard mounts. Deliberately OUTSIDE the SILL-504 launch gate (onboarding
// funnel exposure is a separate concern with its own env override below).
export const MARKETPLACE_FUNNEL_ENABLED_FLAG_KEY = "marketplace_funnel_enabled";

// Plan 289 M8-A0 — gates the consumer header messaging icon + `/threads`
// surfaces (D19). Standalone platform feature, launchable pre-marketplace;
// dev/preview renders unconditionally (see hooks/useMessagingEnabled.ts).
export const MESSAGING_ENABLED_FLAG_KEY = "messaging_enabled";

// Invited funnel (wake-invited work). Mirrors MARKETPLACE_FUNNEL_ENABLED_FLAG_KEY
// above but gates exposure of the dormant `invited` posture variant
// (components/funnel/resolve-posture.ts) instead of the organic marketplace
// funnel. Deliberately a distinct flag key so the two exposure toggles never
// collide.
export const INVITED_FUNNEL_ENABLED_FLAG_KEY = "invited_funnel_enabled";

/**
 * Non-PostHog escape hatch for reaching the funnel while QA-ing the typical
 * path in local + staging, where the PostHog flag often doesn't resolve for
 * an anonymous visitor (locally PostHog frequently isn't initialized at all).
 * Two forms, either enables the funnel for ORGANIC (non-referred) visitors:
 *
 *   - `VITE_MARKETPLACE_FUNNEL_ENABLED=true` — a build-time master switch,
 *     the intended staging knob (set it on the staging web build to walk the
 *     real entry with a `+clerk_test` account + Plaid sandbox).
 *   - `?funnel=1` — a DEV-only ad-hoc query toggle for a single local visit.
 *
 * This never overrides the referral rule: `resolvePosture` still routes any
 * referral to legacy first, so the override only affects organic buyers.
 */
export function funnelTestOverrideEnabled(
  search: URLSearchParams,
  env: { DEV: boolean; VITE_MARKETPLACE_FUNNEL_ENABLED?: string },
): boolean {
  if (env.VITE_MARKETPLACE_FUNNEL_ENABLED === "true") return true;
  return env.DEV === true && search.get("funnel") === "1";
}

/**
 * Non-PostHog escape hatch for reaching the invited funnel while QA-ing it in
 * local + staging, mirroring `funnelTestOverrideEnabled` above with its own
 * env var (`VITE_INVITED_FUNNEL_ENABLED`) and query param (`invited_funnel`)
 * so it never collides with the marketplace override.
 */
export function invitedFunnelTestOverrideEnabled(
  search: URLSearchParams,
  env: { DEV: boolean; VITE_INVITED_FUNNEL_ENABLED?: string },
): boolean {
  if (env.VITE_INVITED_FUNNEL_ENABLED === "true") return true;
  return env.DEV === true && search.get("invited_funnel") === "1";
}
